/**
 * Mock Data Generator for the Applications Overview.
 * Generates realistic data matching the PRD specifications
 */

import type {
  Alert,
  DrilldownFilter,
} from "../components/applicationsOverview/types";

export type { Alert, DrilldownFilter };

// Card type interfaces
export interface NewApplications {
  period: string; // e.g., "24h"
  count: number;
  previousPeriodCount: number;
  minCreatedAt?: string; // ISO 8601
  maxCreatedAt?: string; // ISO 8601
}

export interface PendingApplications {
  count: number;
  minCreatedAt?: string; // ISO 8601
  maxCreatedAt?: string; // ISO 8601
}

export interface UnreadApplications {
  count: number;
  minCreatedAt?: string; // ISO 8601
  maxCreatedAt?: string; // ISO 8601
}

export interface NewActivity {
  period: string; // e.g., "24h"
  count: number;
  previousPeriodCount: number;
  minCreatedAt?: string; // ISO 8601
  maxCreatedAt?: string; // ISO 8601
}

export interface RecentBookings {
  period: string; // e.g., "24h"
  count: number;
  previousPeriodCount: number;
  minCreatedAt?: string; // ISO 8601
  maxCreatedAt?: string; // ISO 8601
}

export interface DecisionMetrics {
  period: string; // e.g., "30d"
  count: number; // Total applications in period
  previousPeriodCount: number; // Total applications in previous period
  autoDecisions: number; // Auto-decisioned applications in period
  previousPeriodAutoDecisions: number; // Auto-decisioned applications in previous period
  approved: number; // Approved applications in period
  previousPeriodApproved: number; // Approved applications in previous period
  denied: number; // Denied applications in period
  previousPeriodDenied: number; // Denied applications in previous period
  canceled: number; // Canceled applications in period
  previousPeriodCanceled: number; // Canceled applications in previous period
}

// Aggregate type definitions matching API response
export type AggregateByDecisionStatus = Array<{
  id: string;
  title: string;
  count: number;
}>;

export type AggregateByFlow = Array<{
  id: string;
  title: string;
  received: number;
  approved: number;
  denied: number;
  canceled: number;
}>;

export interface AggregateByDecisionTime {
  average: number; // Minutes - average time to decision
  previousAverage: number; // Minutes - average from previous period
  distribution: {
    lessThan1d: number;
    oneTo2d: number;
    twoTo4d: number;
    fourTo7d: number;
    moreThan7d: number;
  };
  previousDistribution: {
    lessThan1d: number;
    oneTo2d: number;
    twoTo4d: number;
    fourTo7d: number;
    moreThan7d: number;
  };
}

export interface AggregateByLastActivity {
  average: number; // Minutes - average time since last activity
  distribution: {
    lessThan1d: number;
    oneTo3d: number;
    threeTo7d: number;
    sevenTo14d: number;
    moreThan14d: number;
  };
}

export type AggregateByAdmin = Array<{
  id: number;
  firstName: string;
  lastName: string;
  count: number;
}>;

export type AggregateBySubmissionDate = Array<{
  date: string; // YYYY-MM-DD
  received: number;
  approved: number;
  denied: number;
  canceled: number;
}>;

export interface OverviewMetrics {
  // Card data (base level)
  newApplications: NewApplications;
  pendingApplications: PendingApplications;
  unreadApplications: UnreadApplications;
  newActivity: NewActivity;
  recentBookings: RecentBookings;

  // New aggregate structures
  aggregateByDecisionStatus?: AggregateByDecisionStatus;
  aggregateByFlow?: AggregateByFlow;
  unassignedCount?: number;
  aggregateByDecisionTime?: AggregateByDecisionTime;
  aggregateByLastActivity?: AggregateByLastActivity;
  aggregateByAdmin?: AggregateByAdmin;
  aggregateBySubmissionDate?: AggregateBySubmissionDate;

  decisionMetrics?: DecisionMetrics;

  metadata: {
    timestamp: string;
    cacheUntil?: string;
  };

  alerts: Alert[];
}

const statuses = [
  "Submitted",
  "In Review",
  "Under Review",
  "Pending Approval",
  "Approved",
  "Denied",
  "Incomplete",
  "Awaiting Documents",
];

const flows = [
  "Auto Loan Flow",
  "Personal Loan Flow",
  "Deposit Account Flow",
  "Credit Card Flow",
  "Business Loan Flow",
  "Mortgage Flow",
];

const adminNames = [
  "Sarah Johnson",
  "Michael Chen",
  "Emily Rodriguez",
  "David Kim",
  "Lisa Thompson",
  "James Wilson",
  "Maria Garcia",
  "Robert Brown",
];

function getDateString(daysAgo: number): string {
  const date = new Date();
  date.setDate(date.getDate() - daysAgo);
  return date.toISOString().split("T")[0];
}

function randomInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

export function generateMockOverviewData(): OverviewMetrics {
  const pendingApplications = randomInt(180, 250);
  const yesterday = randomInt(170, 260);
  const pendingApplicationsChange = pendingApplications - yesterday;

  // Generate status distribution
  const aggregateByDecisionStatus: AggregateByDecisionStatus = statuses
    .slice(0, 6)
    .map((status, idx) => {
      const count = Math.floor(
        pendingApplications * [0.25, 0.2, 0.18, 0.15, 0.12, 0.1][idx]
      );
      return {
        id: crypto.randomUUID(),
        title: status,
        count,
      };
    });

  // Generate flow distribution
  const aggregateByFlow: AggregateByFlow = flows
    .slice(0, 5)
    .map((title, idx) => {
      const received = Math.floor(
        pendingApplications * [0.3, 0.25, 0.2, 0.15, 0.1][idx]
      );
      const approved = randomInt(
        Math.floor(received * 0.4),
        Math.floor(received * 0.6)
      );
      const denied = randomInt(
        Math.floor(received * 0.2),
        Math.floor(received * 0.4)
      );
      const canceled = received - approved - denied;
      return {
        id: crypto.randomUUID(),
        title,
        received,
        approved,
        denied,
        canceled,
      };
    });

  // Generate admin workload
  const aggregateByAdmin: AggregateByAdmin = adminNames.map(
    (adminName, idx) => {
      const [firstName, lastName] = adminName.split(" ");
      return {
        id: idx + 1,
        firstName,
        lastName,
        count: randomInt(15, 45),
      };
    }
  );

  // Sort by count for better visualization
  aggregateByAdmin.sort((a, b) => b.count - a.count);

  // Generate volume trend (last 30 days)
  const aggregateBySubmissionDate: AggregateBySubmissionDate = Array.from(
    { length: 30 },
    (_, i) => ({
      date: getDateString(29 - i),
      received: randomInt(15, 45),
      approved: randomInt(5, 20),
      denied: randomInt(5, 15),
      canceled: randomInt(0, 5),
    })
  );

  // Generate decision time distribution
  const lessThan1d = randomInt(150, 180);
  const oneTo2d = randomInt(8, 15);
  const twoTo4d = randomInt(4, 8);
  const fourTo7d = randomInt(2, 5);
  const moreThan7d = randomInt(1, 3);
  const total = lessThan1d + oneTo2d + twoTo4d + fourTo7d + moreThan7d;

  const prevLessThan1d = randomInt(140, 170);
  const prevOneTo2d = randomInt(10, 18);
  const prevTwoTo4d = randomInt(5, 10);
  const prevFourTo7d = randomInt(3, 7);
  const prevMoreThan7d = randomInt(2, 5);
  const prevTotal =
    prevLessThan1d + prevOneTo2d + prevTwoTo4d + prevFourTo7d + prevMoreThan7d;

  const aggregateByDecisionTime: AggregateByDecisionTime = {
    average: randomInt(48 * 60, 96 * 60), // 48-96 hours in minutes
    previousAverage: randomInt(48 * 60, 96 * 60),
    distribution: {
      lessThan1d,
      oneTo2d,
      twoTo4d,
      fourTo7d,
      moreThan7d,
    },
    previousDistribution: {
      lessThan1d: prevLessThan1d,
      oneTo2d: prevOneTo2d,
      twoTo4d: prevTwoTo4d,
      fourTo7d: prevFourTo7d,
      moreThan7d: prevMoreThan7d,
    },
  };

  // Generate aging distribution
  const agingLessThan1d = randomInt(80, 120);
  const agingOneTo3d = randomInt(30, 50);
  const agingThreeTo7d = randomInt(15, 30);
  const agingSevenTo14d = randomInt(5, 15);
  const agingMoreThan14d = randomInt(2, 10);
  const agingTotal =
    agingLessThan1d +
    agingOneTo3d +
    agingThreeTo7d +
    agingSevenTo14d +
    agingMoreThan14d;

  const aggregateByLastActivity: AggregateByLastActivity = {
    average: randomInt(24 * 60, 72 * 60), // 24-72 hours in minutes
    distribution: {
      lessThan1d: agingLessThan1d,
      oneTo3d: agingOneTo3d,
      threeTo7d: agingThreeTo7d,
      sevenTo14d: agingSevenTo14d,
      moreThan14d: agingMoreThan14d,
    },
  };

  // Generate alerts
  const alerts: Alert[] = [];

  // Critical: SLA breaches
  const slaBreachCount = randomInt(5, 15);
  if (slaBreachCount > 0) {
    alerts.push({
      id: "alert-1",
      severity: "critical",
      type: "sla_breach",
      title: "Exceeded SLA Count",
      description:
        "These applications have not been actioned in over 72 hours.",
      affectedCount: slaBreachCount,
      detectedAt: new Date().toISOString(),
      drilldownFilter: {
        customFilter: "sla_exceeded",
        sortBy: "last_activity",
      },
    });
  }

  // Critical: Unassigned applications
  const unassignedCount = randomInt(3, 12);
  alerts.push({
    id: "alert-2",
    severity: "critical",
    type: "unassigned",
    title: "Awaiting Reassignment",
    description:
      "These applications have been submitted but not assigned to an admin.",
    affectedCount: unassignedCount,
    detectedAt: new Date().toISOString(),
    drilldownFilter: {
      customFilter: "unassigned",
    },
  });

  // Warning: Approaching SLA
  const approachingSlaCount = randomInt(8, 20);
  alerts.push({
    id: "alert-3",
    severity: "warning",
    type: "sla_warning",
    title: "Approaching SLA Deadline",
    description:
      "These applications haven't been viewed in 48+ hours and are nearing the 72-hour SLA.",
    affectedCount: approachingSlaCount,
    detectedAt: new Date().toISOString(),
    drilldownFilter: {
      customFilter: "sla_warning",
      sortBy: "last_activity",
    },
  });

  // Unassigned applications (always show if >= 1, severity based on threshold)
  const unassignedThreshold = 10;
  if (unassignedCount >= 1) {
    let severity: "critical" | "warning" | "info";
    let title: string;
    let description: string;

    if (unassignedCount > unassignedThreshold) {
      severity = "critical";
      title = "High Unassigned Applications";
      description = `${unassignedCount} applications are unassigned (threshold: ${unassignedThreshold})`;
    } else if (unassignedCount > unassignedThreshold / 2) {
      severity = "warning";
      title = "Unassigned Applications";
      description = `${unassignedCount} applications are unassigned`;
    } else {
      severity = "info";
      title = "Unassigned Applications";
      description = `${unassignedCount} application${
        unassignedCount === 1 ? " is" : "s are"
      } unassigned`;
    }

    alerts.push({
      id: "unassigned-applications",
      severity,
      type: "workload",
      title,
      description,
      affectedCount: unassignedCount,
      detectedAt: new Date().toISOString(),
      drilldownFilter: {
        customFilter: "unassigned",
      },
    });
  }

  // Warning: Workload imbalance
  const maxWorkload = Math.max(...aggregateByAdmin.map((a) => a.count));
  const minWorkload = Math.min(...aggregateByAdmin.map((a) => a.count));
  if (maxWorkload - minWorkload > 15) {
    const maxAdmin = aggregateByAdmin.find((a) => a.count === maxWorkload);
    const minAdmin = aggregateByAdmin.find((a) => a.count === minWorkload);
    alerts.push({
      id: "alert-4",
      severity: "warning",
      type: "workload_imbalance",
      title: "Uneven Workflow Distribution",
      description: `${maxAdmin?.firstName} ${maxAdmin?.lastName} has ${maxWorkload} apps while ${minAdmin?.firstName} ${minAdmin?.lastName} has ${minWorkload} apps.`,
      affectedCount: maxWorkload - minWorkload,
      detectedAt: new Date().toISOString(),
      drilldownFilter: {},
    });
  }

  // Warning: Status bottleneck
  const reviewStatus = aggregateByDecisionStatus.find(
    (s) => s.title === "In Review"
  );
  if (reviewStatus && reviewStatus.count > 50) {
    alerts.push({
      id: "alert-5",
      severity: "warning",
      type: "status_bottleneck",
      title: "Applications stuck in review",
      description:
        "Applications are spending 4.2 days in this status (expected: 2 days).",
      affectedCount: reviewStatus.count,
      detectedAt: new Date().toISOString(),
      drilldownFilter: {
        decisionStatus: "In Review",
        sortBy: "last_activity",
      },
    });
  }

  // Critical: Failed bookings
  const failedBookings = randomInt(1, 5);
  if (failedBookings > 0) {
    alerts.push({
      id: "alert-6",
      severity: "critical",
      type: "failed_booking",
      title: "Failed to export",
      description:
        "These applications failed when exporting to the core system.",
      affectedCount: failedBookings,
      detectedAt: new Date().toISOString(),
      drilldownFilter: {
        customFilter: "failed_booking",
      },
    });
  }

  // Warning: Stalled applications
  const stalledCount = randomInt(3, 10);
  alerts.push({
    id: "alert-7",
    severity: "warning",
    type: "stalled",
    title: "No recent activity",
    description: "These applications haven't been updated in over 2 weeks.",
    affectedCount: stalledCount,
    detectedAt: new Date().toISOString(),
    drilldownFilter: {
      customFilter: "stalled",
      sortBy: "last_activity",
    },
  });

  // Sort alerts by severity
  alerts.sort((a, b) => {
    const severityOrder = { critical: 0, warning: 1, info: 2 };
    return severityOrder[a.severity] - severityOrder[b.severity];
  });

  // Generate card data
  const newApplicationsCount = randomInt(5, 25);
  const newApplicationsPreviousCount = randomInt(5, 25);
  const unreadApplicationsCount = randomInt(3, 30);
  const newActivityCount = randomInt(5, 40);
  const newActivityPreviousCount = randomInt(5, 40);
  const recentBookingsCount = randomInt(5, 25);
  const recentBookingsPreviousCount = randomInt(5, 25);

  return {
    // Card data (base level)
    newApplications: {
      period: "24h",
      count: newApplicationsCount,
      previousPeriodCount: newApplicationsPreviousCount,
      minCreatedAt: new Date(
        Date.now() - 1000 * 60 * randomInt(5 * 60, 24 * 60)
      ).toISOString(),
      maxCreatedAt: new Date(
        Date.now() - 1000 * 60 * randomInt(5, 12 * 60)
      ).toISOString(),
    },
    pendingApplications: {
      count: pendingApplications,
      minCreatedAt: new Date(
        Date.now() - 1000 * 60 * randomInt(12 * 60, 168 * 60)
      ).toISOString(),
      maxCreatedAt: new Date(
        Date.now() - 1000 * 60 * randomInt(5, 24 * 60)
      ).toISOString(),
    },
    unreadApplications: {
      count: unreadApplicationsCount,
      minCreatedAt: new Date(
        Date.now() - 1000 * 60 * randomInt(24 * 60, 168 * 60)
      ).toISOString(),
      maxCreatedAt: new Date(
        Date.now() - 1000 * 60 * randomInt(30, 48 * 60)
      ).toISOString(),
    },
    newActivity: {
      period: "24h",
      count: newActivityCount,
      previousPeriodCount: newActivityPreviousCount,
      minCreatedAt: new Date(
        Date.now() - 1000 * 60 * randomInt(24 * 60, 168 * 60)
      ).toISOString(),
      maxCreatedAt: new Date(
        Date.now() - 1000 * 60 * randomInt(15, 24 * 60)
      ).toISOString(),
    },
    recentBookings: {
      period: "24h",
      count: recentBookingsCount,
      previousPeriodCount: recentBookingsPreviousCount,
      minCreatedAt: new Date(
        Date.now() - 1000 * 60 * randomInt(5, 24 * 60)
      ).toISOString(),
      maxCreatedAt: new Date(
        Date.now() - 1000 * 60 * randomInt(5, 120)
      ).toISOString(),
    },

    // New aggregate structures
    aggregateByDecisionStatus,
    aggregateByFlow,
    unassignedCount,
    aggregateByDecisionTime,
    aggregateByLastActivity,
    aggregateByAdmin,
    aggregateBySubmissionDate,

    decisionMetrics: (() => {
      const count = randomInt(150, 250);
      const previousCount = randomInt(140, 260);
      const approved = randomInt(
        Math.floor(count * 0.5),
        Math.floor(count * 0.7)
      );
      const denied = randomInt(
        Math.floor(count * 0.2),
        Math.floor(count * 0.35)
      );
      const canceled = count - approved - denied;
      const autoDecisions = randomInt(
        Math.floor(count * 0.6),
        Math.floor(count * 0.85)
      );

      const prevApproved = randomInt(
        Math.floor(previousCount * 0.5),
        Math.floor(previousCount * 0.7)
      );
      const prevDenied = randomInt(
        Math.floor(previousCount * 0.2),
        Math.floor(previousCount * 0.35)
      );
      const prevCanceled = previousCount - prevApproved - prevDenied;
      const prevAutoDecisions = randomInt(
        Math.floor(previousCount * 0.6),
        Math.floor(previousCount * 0.85)
      );

      return {
        period: "30d",
        count,
        previousPeriodCount: previousCount,
        autoDecisions,
        previousPeriodAutoDecisions: prevAutoDecisions,
        approved,
        previousPeriodApproved: prevApproved,
        denied,
        previousPeriodDenied: prevDenied,
        canceled,
        previousPeriodCanceled: prevCanceled,
      };
    })(),

    metadata: {
      timestamp: new Date().toISOString(),
      cacheUntil: new Date(Date.now() + 30000).toISOString(), // Cache for 30 seconds
    },

    alerts,
  };
}

// Export a singleton instance for consistent data during development
let cachedData: OverviewMetrics | null = null;
let cacheTime = 0;
const CACHE_DURATION = 30000; // 30 seconds

export function getMockOverviewData(forceRefresh = false): OverviewMetrics {
  const now = Date.now();
  if (forceRefresh || !cachedData || now - cacheTime > CACHE_DURATION) {
    cachedData = generateMockOverviewData();
    cacheTime = now;
  }
  return cachedData;
}
